import api from './api';

// Types
export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total?: number;
}

export interface Invoice {
  id: string;
  client_id: string;
  invoice_number: string;
  amount: number;
  items: InvoiceItem[];
  due_date: string;
  notes?: string;
  status: string;
  pdf_url?: string;
  payment_link?: string;
  stripe_invoice_id?: string;
  paid_at?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  client_name?: string;
  client_email?: string;
  client_company?: string;
  client_address?: string;
}

export interface CreateInvoiceData {
  clientId: string;
  items: InvoiceItem[];
  dueDate: string;
  notes?: string;
}

export interface InvoicesResponse {
  success: boolean;
  data: {
    data: Invoice[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface GetInvoicesParams {
  page?: number;
  limit?: number;
  search?: string;
  clientId?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Status options
export const invoiceStatusOptions = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];

// Service methods
export const invoicesService = {
  // Get all invoices with pagination
  async getInvoices(params: GetInvoicesParams = {}): Promise<{ data: Invoice[]; pagination: { total: number; page: number; limit: number; totalPages: number } }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.clientId) queryParams.append('clientId', params.clientId);
    if (params.status) queryParams.append('status', params.status);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await api.get<InvoicesResponse>(`/invoices?${queryParams.toString()}`);
    
    // Parse items JSON for each invoice
    const invoices = response.data.data.data.map(invoice => ({
      ...invoice,
      items: typeof invoice.items === 'string' ? JSON.parse(invoice.items) : invoice.items,
    }));
    
    return {
      data: invoices,
      pagination: {
        total: response.data.data.total,
        page: response.data.data.page,
        limit: response.data.data.limit,
        totalPages: response.data.data.totalPages,
      },
    };
  },

  // Get single invoice
  async getInvoice(id: string): Promise<Invoice> {
    const response = await api.get<{ success: boolean; data: Invoice }>(`/invoices/${id}`);
    const invoice = response.data.data;
    return {
      ...invoice,
      items: typeof invoice.items === 'string' ? JSON.parse(invoice.items) : invoice.items,
    };
  },

  // Create new invoice
  async createInvoice(data: CreateInvoiceData): Promise<Invoice> {
    const response = await api.post<{ success: boolean; data: Invoice }>('/invoices', data);
    return response.data.data;
  },

  // Generate PDF for invoice
  async generatePDF(id: string): Promise<{ pdfUrl: string; filename: string }> {
    const response = await api.post<{ success: boolean; data: { pdfUrl: string; filename: string } }>(`/invoices/${id}/generate-pdf`);
    return response.data.data;
  },

  // Send invoice via email
  async sendInvoice(id: string): Promise<{ messageId: string; status: string }> {
    const response = await api.post<{ success: boolean; data: { messageId: string; status: string } }>(`/invoices/${id}/send`);
    return response.data.data;
  },

  // Update invoice status
  async updateStatus(id: string, status: string): Promise<Invoice> {
    const response = await api.put<{ success: boolean; data: Invoice }>(`/invoices/${id}/status`, { status });
    return response.data.data;
  },

  // Download invoice PDF (returns blob URL)
  async downloadPDF(pdfUrl: string): Promise<string> {
    const response = await api.get(pdfUrl, { responseType: 'blob' });
    return URL.createObjectURL(response.data);
  },
};

export default invoicesService;
